import { useState } from 'react'
import Select from 'react-select'
import type { GroupBase, OptionProps } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { useTranslations } from 'next-intl'
import { Toast } from '@skop-app/lib-front'

import { filterOptionsByProject } from '@/hooks/gridOptionLookups'
import { useSelectEditor } from '../useSelectEditor'

import { CreateLabel } from './CreateLabel'
import { SelectOption } from './SelectOption'
import { InlineEditInput } from './InlineEditInput'
import { useEditorOption } from './useEditorOption'
import { useInlineEdit } from './useInlineEdit'
import type {
  EditorOption,
  EditorOptionNode,
  SelectCreateEditorProps,
} from './types'
import { findMatchingReferenceOptions, sanitizeCreateLabel } from './utils'

export function SelectCreateEditor({
  onValueChange,
  stopEditing,
  api,
  colDef,
  data,
  eGridCell,
  context,
  value,
}: SelectCreateEditorProps) {
  const t = useTranslations('Toolbar')
  const userId = context.ownership?.byUserID
  const [inputValue, setInputValue] = useState('')

  const { field, minWidth, baseSelectProps } = useSelectEditor({
    eGridCell,
    colDef,
  })
  const closeEditor = () => {
    api.stopEditing()
  }
  const { cellEditorParams = {} } = colDef
  const fieldKey = field
  const property = cellEditorParams?.property
  const isMulti = property?.isMulti ?? false
  const isRef = property?.isRef ?? false

  const onMandatoryFieldEmpty = () => {
    Toast.error(t('select-mandatory-field'))
  }

  const rawOptions =
    isRef && property?.view !== 'references'
      ? ((context.refOptions ?? []) as EditorOptionNode[])
      : ((property?.key === 'shapeID'
          ? filterOptionsByProject(
              (context.propertyDisplayOptions?.shapeID ??
                context.propertyOptions?.shapeID ??
                []) as EditorOptionNode[],
              context.currentProjectID,
            )
          : (context.propertyDisplayOptions?.[property?.key ?? ''] ??
            context.propertyOptions?.[property?.key ?? ''] ??
            [])) as EditorOptionNode[])
  const options = Array.isArray(rawOptions) ? rawOptions : []

  const {
    handleChange,
    handleCreate,
    canCreate,
    canUpdate,
    isCreating,
    isAwaitingGroupSelection,
    createError,
    clearCreateError,
    refreshReferences,
    isRefreshing,
  } = useEditorOption({
    fieldKey,
    property,
    context,
    availableOptions: options,
    currentValue: value,
    onValueChange,
    stopEditing: closeEditor,
    onMandatoryFieldEmpty,
    createErrorMessage: t('select-create-error'),
    noGroupsMessage: t('select-create-no-groups'),
    existingReferencesMessage: t('select-existing-references'),
    ambiguousNameMessage: t('select-ambiguous-name'),
    onCreateCommitted: () => setInputValue(''),
  })

  const {
    isEditing,
    editValue,
    setEditValue,
    startEditing,
    submitEdit,
    handleKeyDown,
  } = useInlineEdit({
    stopEditing,
    canUpdate,
    context,
    property,
    api,
  })

  const optionLookup = new Map<string, EditorOptionNode>()
  const indexOptions = (nodes: EditorOptionNode[]) => {
    for (const node of nodes) {
      if (node.id) optionLookup.set(node.id, node)
      optionLookup.set(node.value, node)
      if (node.options) {
        indexOptions(node.options)
      }
    }
  }
  indexOptions(options)

  const resolveOption = (raw: unknown): EditorOptionNode | undefined => {
    if (typeof raw === 'string') {
      const matchedOption = optionLookup.get(raw)
      if (matchedOption) return matchedOption

      const referenceLabel =
        isRef && property?.view === 'inventory'
          ? context.referenceLabelsById?.[raw]
          : undefined
      if (referenceLabel) {
        return {
          id: raw,
          value: raw,
          label: referenceLabel,
        }
      }

      return undefined
    }
    if (raw && typeof raw === 'object' && 'value' in raw) {
      const entry = raw as EditorOption
      return (
        optionLookup.get(entry.id ?? entry.value) ?? (entry as EditorOptionNode)
      )
    }
    return undefined
  }

  const selectedValue =
    isRef &&
    property?.view === 'inventory' &&
    data &&
    typeof data === 'object' &&
    'referenceID' in data &&
    typeof data.referenceID === 'string'
      ? data.referenceID
      : value
  const selectedEntries = Array.isArray(selectedValue)
    ? selectedValue
    : selectedValue != null
      ? [selectedValue]
      : []
  const resolvedSelectValue = selectedEntries
    .map(resolveOption)
    .filter((entry): entry is EditorOptionNode => Boolean(entry))
  const selectValue = isMulti
    ? resolvedSelectValue
    : (resolvedSelectValue[0] ?? null)

  const handleEditClick = (
    event: React.MouseEvent<SVGSVGElement>,
    optionLabel: string,
    optionId: string,
  ) => {
    event.stopPropagation()
    startEditing(optionLabel, optionId)
  }

  const OptionComponent = (
    props: OptionProps<EditorOption, boolean, GroupBase<EditorOption>>,
  ) => (
    <SelectOption
      {...props}
      {...(canUpdate && {
        onEditClick: (event) =>
          handleEditClick(
            event,
            props.label,
            props.data.id ?? props.data.value,
          ),
      })}
      userId={userId}
    />
  )

  const isReferencePicker = isRef && property?.view !== 'references'
  const isValidNewOption = (nextValue: string) =>
    sanitizeCreateLabel(nextValue).length > 0 &&
    !isRefreshing &&
    (!isReferencePicker ||
      findMatchingReferenceOptions(options, nextValue).length === 0)

  const SelectComponent = canCreate ? CreatableSelect : Select
  const placeholder = isRef ? t('select-ref') : t('select-option')

  const handleSelectChange = (event: EditorOption | EditorOption[] | null) => {
    clearCreateError()
    setInputValue('')
    handleChange(event)
  }

  const handleCreateOption = async (nextValue: string) => {
    await handleCreate(nextValue)
  }

  const handleInputChange = (nextValue: string, meta: { action: string }) => {
    if (meta.action === 'input-change') {
      clearCreateError()
      setInputValue(nextValue)
    }
    return nextValue
  }

  if (isEditing && canUpdate) {
    return (
      <InlineEditInput
        value={editValue}
        onChange={setEditValue}
        onBlur={submitEdit}
        onKeyDown={handleKeyDown}
        minWidth={minWidth}
      />
    )
  }

  return (
    <div className="min-w-0" data-testid={`grid-select-editor-${fieldKey}`}>
      <SelectComponent
        {...baseSelectProps}
        classNamePrefix="react-select"
        inputId={fieldKey}
        name={fieldKey}
        onChange={handleSelectChange}
        value={selectValue}
        options={options}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        isDisabled={isCreating || isAwaitingGroupSelection}
        isLoading={isCreating || isRefreshing}
        onMenuOpen={isReferencePicker ? refreshReferences : undefined}
        {...(canCreate && {
          onCreateOption: handleCreateOption,
          isValidNewOption,
          formatCreateLabel: (nextValue: string) => (
            <CreateLabel
              inputValue={
                isReferencePicker
                  ? t('select-create-reference', { label: nextValue })
                  : t('select-create-new-option', { label: nextValue })
              }
            />
          ),
        })}
        placeholder={placeholder}
        noOptionsMessage={() => t('no-options')}
        components={{
          ...baseSelectProps.components,
          Option: OptionComponent,
        }}
        classNames={{
          menu: () => 'ag-custom-component-popup',
          menuPortal: () => 'ag-custom-component-popup',
        }}
        isOptionDisabled={(option) =>
          isRefreshing || (option as EditorOption).isSelectable === false
        }
        isMulti={isMulti}
        openMenuOnFocus={isMulti}
        closeMenuOnSelect={!isMulti}
      />
      {createError ? (
        <div className="mt-1 text-xs text-red-600">{createError}</div>
      ) : null}
    </div>
  )
}
